import type { Melody, MelodyChordLane, Scale } from '@tunasong/schemas'
import { bassPitchClass, voiceChord } from './chord-voicing.js'
import { barBeats, beatMs, GRID_TOLERANCE } from './grid.js'
import type { ScheduledMelodyEvent } from './schedule.js'

/**
 * The chords under a melody, as events for the same absolute-time scheduler the melody plays on
 * (melody toolkit plan M2 step 2.3; spec §5.1). Pure, like `scheduleMelody`.
 *
 * Each chord sounds when the lane says it does, not one bar each, voiced by Bowie's rule against the
 * lowest melody note sounding in its span. A chord longer than a bar is struck again on each downbeat
 * inside it, because a piano decays. Chords play at half the melody's velocity.
 */

export interface ScheduleChordsOptions {
  /**
   * False when the melody is muted: the voicing then needs no room under it, so the full voicing comes
   * back (Bowie). Defaults to true.
   */
  isMelodyOn?: boolean
  /**
   * Beats of count-in before beat 1 (spec §5.2, Sing an idea): the first chord sounds through them, and
   * the lane is shifted after them.
   */
  leadInBeats?: number
}

/** A melody with no notes yet (Chords only) still needs a level: the converted-suggestion default */
const defaultMelodyVelocity = 0.8

/** Half the melody's mean velocity */
const chordVelocity = (melody: Melody) => {
  const mean =
    melody.notes.length === 0
      ? defaultMelodyVelocity
      : melody.notes.reduce((total, note) => total + note.velocity, 0) / melody.notes.length
  return mean / 2
}

/** The lowest melody note sounding anywhere in [from, to), held-in notes included */
const lowestIn = (melody: Melody, from: number, to: number) => {
  const sounding = melody.notes.filter(
    note => note.onset < to - GRID_TOLERANCE && note.onset + note.duration > from + GRID_TOLERANCE
  )
  return sounding.length === 0 ? undefined : Math.min(...sounding.map(note => note.midi))
}

/** Where a chord is struck: its onset, then each downbeat inside it */
const strikesOf = (onset: number, duration: number, bar: number) => {
  const end = onset + duration
  const strikes = [onset]
  for (
    let downbeat = (Math.floor(onset / bar + GRID_TOLERANCE) + 1) * bar;
    downbeat < end - GRID_TOLERANCE;
    downbeat += bar
  ) {
    strikes.push(downbeat)
  }
  return strikes.map((at, index) => ({ at, length: (strikes[index + 1] ?? end) - at }))
}

export const scheduleChords = (
  melody: Melody,
  lane: MelodyChordLane,
  options: ScheduleChordsOptions
): ScheduledMelodyEvent[] => {
  const { isMelodyOn = true, leadInBeats = 0 } = options
  const ms = beatMs(melody.tempo.bpm)
  const bar = barBeats(melody.tempo.meter)
  const velocity = chordVelocity(melody)
  /**
   * The count-in sounds the chord on beat 0 and the lane strikes it again on beat 1 (Bowie). With no chord
   * at beat 0 the count-in is the click alone; an N.C. there voices as silence anyway.
   */
  const [first] = lane.chords
  const isOnBeatZero = first !== undefined && first.onset < GRID_TOLERANCE
  const leadIn =
    first && isOnBeatZero && leadInBeats > 0
      ? [{ symbol: first.symbol, onset: -leadInBeats, duration: leadInBeats }]
      : []
  return [...leadIn, ...lane.chords].flatMap(chord => {
    const melodyLow = isMelodyOn ? lowestIn(melody, chord.onset, chord.onset + chord.duration) : undefined
    const voicing = voiceChord(chord.symbol, melodyLow)
    if (!voicing) {
      return []
    }
    const notes = [voicing.bass, ...voicing.upper]
    /** The lead-in is struck once: its beats sit before bar 1, outside the bar lines */
    const strikes =
      chord.onset < 0 ? [{ at: chord.onset, length: chord.duration }] : strikesOf(chord.onset, chord.duration, bar)
    return strikes.flatMap(({ at, length }) =>
      notes.map(midi => ({ time: (at + leadInBeats) * ms, midi, velocity, durationMs: length * ms }))
    )
  })
}

/** The drone's root sits in C2–B2, like a chord's bass */
const droneBassLow = 36
const fifth = 7

/**
 * Spec §7, §8: with no chords, the key's root and the 5th above it, held for `beats`. It decides neither
 * major nor minor (Bowie). An unknown root is silence.
 */
export const scheduleDrone = (melody: Melody, key: Scale, beats: number): ScheduledMelodyEvent[] => {
  const pitchClass = bassPitchClass(key.root)
  if (pitchClass === undefined) {
    return []
  }
  const root = droneBassLow + pitchClass
  const durationMs = beats * beatMs(melody.tempo.bpm)
  const velocity = chordVelocity(melody)
  return [root, root + fifth].map(midi => ({ time: 0, midi, velocity, durationMs }))
}
